import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';
import {
  ICumulativeLoginStreakResponse,
  ICumulativeLoginRewardBand,
  ICumulativeLoginNextReward,
} from 'models/vip/i-cumulative-login.interface';

import { getCumulativeLoginStreak } from 'services/cumulative-login.service';

import { queryKeys } from './query-keys';

export interface UseCumulativeLoginStreakReturn {
  currentStreak: number;
  longestStreak: number;
  lastPresenceDate: string | null;
  lastClaimDate: string | null;
  isPresentToday: boolean;
  isClaimedToday: boolean;
  /** The streak-day band the user is currently in (null when below the first band). */
  currentReward: ICumulativeLoginRewardBand | null;
  /** current_reward != null AND !is_claimed_today — today's reward can be claimed now. */
  isRewardClaimable: boolean;
  /** The next band + how many more daily check-ins are needed to reach it. */
  nextReward: ICumulativeLoginNextReward | null;
  /** Explanatory copy when there's no claimable band (e.g. non-VIP: VIP-only feature). */
  message: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const fetchCumulativeLoginStreak = async (): Promise<ICumulativeLoginStreakResponse> => {
  const response = await getCumulativeLoginStreak(new AbortController().signal);
  return response.data;
};

export const useCumulativeLoginStreakQuery = (): UseCumulativeLoginStreakReturn => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.cumulativeLogin.streak(walletAddress),
    queryFn: fetchCumulativeLoginStreak,
    enabled: !!walletAddress,
    staleTime: 30000,
    retry: 1,
  });

  return {
    currentStreak: data?.current_streak ?? 0,
    longestStreak: data?.longest_streak ?? 0,
    lastPresenceDate: data?.last_presence_date ?? null,
    lastClaimDate: data?.last_claim_date ?? null,
    isPresentToday: data?.is_present_today ?? false,
    isClaimedToday: data?.is_claimed_today ?? false,
    currentReward: data?.current_reward ?? null,
    isRewardClaimable: data?.is_reward_claimable ?? false,
    nextReward: data?.next_reward ?? null,
    message: data?.message ?? null,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch cumulative login streak') : null,
    refetch,
  };
};
